import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';

type UndoRedoAtom<Action = unknown> = {
  undoActions: readonly Action[];
  redoActions: readonly Action[];
};

const undoRedoAtomFamily = atomFamily((key: string) =>
  atom<UndoRedoAtom>({
    undoActions: [],
    redoActions: [],
  }),
);

type UseUndoRedo<Action> = {
  undo: () => void;
  redo: () => void;
  addAction: (action: Action) => void;
};

type UseUndoRedoArgs<Action> = {
  key: string;
  getReverseAction: (action: Action) => Action | null;
  performAction: (action: Action) => void;
};

// TODO: Add useCallback where necessary
// TODO: If a reverse command cannot be found, does that mean we should disable further undos/redos?
//   aka set that list of actions to empty?
export const useUndoRedo = <Action>({
  key,
  getReverseAction,
  performAction,
}: UseUndoRedoArgs<Action>): UseUndoRedo<Action> => {
  const [_state, setState] = useAtom(undoRedoAtomFamily(key));
  // Couldn't figure out how to pass Action generic to atomFamily
  const state = _state as UndoRedoAtom<Action>;

  return {
    undo: useCallback(() => {
      const toUndoAction = state.undoActions[state.undoActions.length - 1];
      if (!toUndoAction) {
        return;
      }

      const reverseAction = getReverseAction(toUndoAction);
      if (!reverseAction) {
        // Disable further undos because one failed. Seems like a reasonable heuristic.
        setState({
          undoActions: [],
          redoActions: state.redoActions,
        });
        return;
      }

      performAction(toUndoAction);
      setState({
        undoActions: state.undoActions.slice(0, -1),
        redoActions: [...state.redoActions, reverseAction],
      });
    }, [state, setState, getReverseAction, performAction]),
    redo: useCallback(() => {
      const toRedoAction = state.redoActions[state.redoActions.length - 1];
      if (!toRedoAction) {
        return;
      }

      const reverseAction = getReverseAction(toRedoAction);
      if (!reverseAction) {
        // Disable further redos because one failed. Seems like a reasonable heuristic.
        setState({
          undoActions: state.undoActions,
          redoActions: [],
        });
        return;
      }

      performAction(toRedoAction);
      setState({
        undoActions: [...state.undoActions, reverseAction],
        redoActions: state.redoActions.slice(0, -1),
      });
    }, [state, setState, getReverseAction, performAction]),
    addAction: useCallback(
      (action) => {
        const reverseAction = getReverseAction(action);
        if (!reverseAction) {
          console.error('No reverse action found for action', action);
          return;
        }

        setState((state) => ({
          undoActions: [...state.undoActions, reverseAction],
          redoActions: [],
        }));
      },
      [setState, getReverseAction],
    ),
  };
};
